import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canShowAppleIAP } from '../../shared/utils/platform';
import { getCachedAuthUser } from '../../services/network';
import { useSubscription } from './useSubscription';
import { useEntitlement } from './useEntitlement';

/**
 * useLapsedRedirect — mounted once in Layout (docs/v150-spec.md §3.5).
 *
 * When Premium has just ended — an Apple trial or subscription, a referral
 * pass or the old preview, as the server's `lapsed` says — send the user to
 * the plans ONCE: /paywall?src=lapsed&reason=<kind>. Only on iOS (the only
 * place Premium can be bought), only on Free with no Premium shared by a
 * baby's owner, only within 30 days of the end, and never while the user is
 * already on the paywall, in onboarding or on the offer page.
 *
 * It waits for useEntitlement's `settled` (v1.5.1, docs/v151-spec.md §6.2):
 * the own status, the babies list and the baby-scoped plan must all have
 * answered. A list still loading, a baby not yet selected, a failed or
 * offline-paused fetch would otherwise read as "not shared" and send a
 * caregiver covered by the owner's Premium to the paywall.
 *
 * The acknowledgement is stored per Cognito sub and per endedAt — another
 * account on the device, or a later lapse, gets its own redirect. It is written
 * when the redirect fires, so closing the app on the paywall does not bring
 * the user straight back to it.
 */
export const LAPSED_ACK_PREFIX = 'bt_lapsed_ack:';
const WINDOW_MS = 30 * 86400000;
const KINDS = ['trial', 'subscription', 'pass', 'preview'];
const QUIET_PATHS = ['/paywall', '/onboarding', '/offer'];

function readAck(sub) {
  try {
    return localStorage.getItem(`${LAPSED_ACK_PREFIX}${sub}`);
  } catch {
    return null;
  }
}

function writeAck(sub, endedAt) {
  try {
    localStorage.setItem(`${LAPSED_ACK_PREFIX}${sub}`, endedAt);
    return true;
  } catch {
    return false;
  }
}

export function useLapsedRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { plan, lapsed, isLoading } = useSubscription();
  const { sharedByOwner, isLoading: entitlementLoading, settled } = useEntitlement();

  const kind = lapsed?.kind;
  const endedAt = lapsed?.endedAt || null;

  useEffect(() => {
    if (!canShowAppleIAP() || isLoading || entitlementLoading || !settled) return;
    if (plan !== 'free' || sharedByOwner || !endedAt || !KINDS.includes(kind)) return;
    if (QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;
    const endedMs = Date.parse(endedAt);
    if (!Number.isFinite(endedMs) || Date.now() - endedMs > WINDOW_MS) return;
    const sub = getCachedAuthUser()?.sub;
    if (!sub || readAck(sub) === endedAt) return;
    // No storage → no way to remember it: better never than on every launch.
    if (!writeAck(sub, endedAt)) return;
    navigate(`/paywall?src=lapsed&reason=${kind}`);
  }, [plan, kind, endedAt, sharedByOwner, isLoading, entitlementLoading, settled, pathname, navigate]);
}

export default useLapsedRedirect;
